import React from 'react';

import {
  CheckboxList,
  type CheckboxListBatchProps,
  type CheckboxListItem,
} from './CheckboxList';

/**
 * Why a private template cannot join a public lens. The API and the database
 * both refuse it; saying so on the item is what keeps the save from being the
 * first anyone hears of it.
 */
export const PRIVATE_TEMPLATE_ON_PUBLIC_LENS = 'Private. Publish it, or keep this lens private, to include it.';

/** One attachable template, from either the member or the System Admin endpoint. */
export interface LensTemplateOption {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  isPublic: boolean;
  /** Owned by the lens's organization — decides the section. */
  isOwn: boolean;
  organizationName: string | null;
  /** Member reads only; absent on the System Admin template row. */
  hasApprovedVersion?: boolean;
}

interface LensTemplateSelectorProps {
  /** Pins (selected, not loaded) first, then the loaded rows. The page builds this. */
  options: LensTemplateOption[];
  lensIsPublic: boolean;
  selectedIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  disabled?: boolean;
  /** Under the legend (System Admin names the owning organization here). */
  description?: React.ReactNode;
  /** From `checkboxListProps(templatesList)`. */
  listProps: CheckboxListBatchProps;
  emptyMessage?: React.ReactNode;
  idPrefix?: string;
}

const toItem = (option: LensTemplateOption, lensIsPublic: boolean): CheckboxListItem => {
  const notes: string[] = [];
  if (!option.isOwn && option.organizationName) notes.push(`by ${option.organizationName}`);
  if (option.hasApprovedVersion === false) notes.push('no approved version yet');
  const hasDescription = notes.length > 0 || Boolean(option.description);
  return {
    id: option.id,
    label: option.name,
    meta: option.slug,
    description: hasDescription ? (
      <>
        {notes.length > 0 ? (
          <span className="flex flex-wrap gap-x-2">
            {notes.map((note) => (
              <span key={note}>{note}</span>
            ))}
          </span>
        ) : null}
        {option.description ? <span className="line-clamp-2 block">{option.description}</span> : null}
      </>
    ) : undefined,
    searchText: [option.organizationName, option.description].filter(Boolean).join(' '),
    disabledReason: lensIsPublic && !option.isPublic ? PRIVATE_TEMPLATE_ON_PUBLIC_LENS : undefined,
  };
};

/**
 * The lens template picker, on My Lenses and System Admin → All Lenses alike:
 * the lens's own organization's templates, then everything other organizations
 * have published, with the public-lens / private-template rule on each item.
 * The pages own the reads (their endpoints and row types differ); this owns
 * the presentation and the rule.
 */
export const LensTemplateSelector: React.FC<LensTemplateSelectorProps> = ({
  options, lensIsPublic, selectedIds, onToggle, disabled, description, listProps, emptyMessage, idPrefix,
}) => (
  <CheckboxList
    legend="Processor templates"
    itemNoun="templates"
    searchPlaceholder="Search templates…"
    description={description}
    sections={[
      {
        key: 'own',
        heading: 'Your organization',
        items: options.filter((o) => o.isOwn).map((o) => toItem(o, lensIsPublic)),
      },
      {
        key: 'public',
        heading: 'Published by other organizations',
        items: options.filter((o) => !o.isOwn && o.isPublic).map((o) => toItem(o, lensIsPublic)),
      },
    ]}
    selectedIds={selectedIds}
    onToggle={onToggle}
    disabled={disabled}
    idPrefix={idPrefix}
    emptyMessage={emptyMessage}
    {...listProps}
  />
);
